import { useEffect, useState, useRef } from 'react';

export default function Hero() {
  const [scrollY, setScrollY] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const heroRef = useRef<HTMLElement>(null);

  useEffect(() => {
    // ページ読み込み後にアニメーション開始
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 100);
    
    const handleScroll = () => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        if (rect.bottom > 0) {
          setScrollY(window.scrollY);
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timer);
    };
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section ref={heroRef} className="relative min-h-[50vh] flex items-center overflow-hidden pt-16">
      {/* Animated Background Image with Parallax */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute inset-0 transition-all duration-[3000ms] ease-out"
          style={{
            transform: `scale(${isLoaded ? 1.15 : 1}) translateY(${scrollY * 0.3}px)`,
          }}
        >
          <img
            src="https://static.readdy.ai/image/8d13d909127b965a1b96285c4b9af1e2/5ad00d36a2330881e3c0eddf9f596de0.jpeg"
            alt="きらり保育園箕面園 スタッフ募集"
            className={`w-full h-[120%] object-cover object-[25%_center] md:object-center transition-opacity duration-1000 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
          />
        </div>
        
        {/* Animated Gradient Overlay */}
        <div 
          className="absolute inset-0 bg-gradient-to-r from-white/70 via-white/50 to-white/30 transition-transform duration-300"
          style={{
            transform: `translateY(${scrollY * 0.1}px)`,
          }}
        ></div>
        
        {/* Floating Light Particles Effect */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-teal-200/20 rounded-full blur-3xl animate-[pulse_4s_ease-in-out_infinite]"></div>
          <div className="absolute bottom-1/3 right-1/4 w-48 h-48 bg-amber-200/20 rounded-full blur-3xl animate-[pulse_4s_ease-in-out_1s_infinite]"></div>
          <div className="absolute top-1/2 right-1/3 w-32 h-32 bg-cyan-200/20 rounded-full blur-2xl animate-[pulse_4s_ease-in-out_2s_infinite]"></div>
        </div>
      </div>

      <div 
        className="max-w-7xl mx-auto px-6 py-8 w-full relative z-10 transition-transform duration-300"
        style={{
          transform: `translateY(${scrollY * -0.1}px)`,
        }}
      >
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          {/* Left Content - Empty space to show background */}
          <div></div>
          
          {/* Right Content with Fade-in Animation */}
          <div className={`space-y-4 transition-all duration-1000 delay-300 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {/* 箕面園専用バッジ */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-block bg-amber-500 text-white px-3 py-1.5 rounded-full text-sm font-bold shadow-lg hover:scale-105 transition-transform cursor-default">
                <i className="ri-map-pin-2-fill mr-1"></i>
                きらり保育園箕面園 専用
              </div>
              <div className="inline-block bg-teal-600 text-white px-3 py-1.5 rounded-full text-sm font-medium shadow-lg hover:scale-105 transition-transform cursor-default">
                保育士・看護師・調理補助 募集
              </div>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
              「働いてみないとわからない」<br />
              <span className="text-teal-600">
                そんな本音に応える場所。
              </span>
            </h1>
            
            <p className="text-base text-gray-700 leading-relaxed max-w-xl">
              <strong className="text-teal-700">きらり保育園箕面園</strong>で、一生の職場探しも、スキマ時間を活用した副業も。まずはカジュアルに「おためし」から始めてみませんか？
            </p>
            
            {/* 所在地情報 */}
            <div className="bg-white/90 backdrop-blur-sm rounded-lg p-4 inline-block shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-start gap-3">
                <i className="ri-map-pin-line text-teal-600 text-xl mt-0.5 animate-bounce"></i>
                <div className="space-y-2">
                  <p className="text-sm text-gray-800 font-medium leading-relaxed">
                    〒562-0032<br />
                    大阪府箕面市小野原東3丁目9-29<br />
                    ラクシュリ小野原2F
                  </p>
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=大阪府箕面市小野原東3丁目9-29+ラクシュリ小野原2F"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-teal-600 hover:text-teal-700 font-medium cursor-pointer transition-colors group"
                  >
                    <i className="ri-map-2-line group-hover:animate-pulse"></i>
                    Googleマップで開く
                    <i className="ri-external-link-line group-hover:translate-x-0.5 transition-transform"></i>
                  </a>
                </div>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-4">
              <button
                onClick={() => scrollToSection('information')}
                className="bg-cyan-500 text-white px-6 py-3 rounded-lg text-sm font-semibold hover:bg-cyan-600 transition-all transform hover:scale-105 hover:-translate-y-0.5 cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl inline-flex items-center gap-2 group"
              >
                さっそくスポットワーク
                <i className="ri-arrow-right-line text-lg group-hover:translate-x-1 transition-transform"></i>
              </button>
              
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-1.5 text-gray-700 hover:text-teal-600 transition-colors">
                  <i className="ri-check-line text-teal-600"></i>
                  <span className="text-xs font-medium">履歴書不要</span>
                </div>
                <div className="flex items-center gap-1.5 text-gray-700 hover:text-teal-600 transition-colors">
                  <i className="ri-check-line text-teal-600"></i>
                  <span className="text-xs font-medium">私服でOK</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
      `}</style>
    </section>
  );
}
